import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ArtImage } from "./ArtImage";

export const ArtworkCarousel = ({ artworks, onOpen, testIdPrefix = "carousel" }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: "start", containScroll: "trimSnaps" });
  const [selected, setSelected] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelected(emblaApi.selectedScrollSnap());
    setCanPrev(emblaApi.canScrollPrev());
    setCanNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  if (!artworks.length) return null;

  return (
    <div data-testid={`${testIdPrefix}-carousel`}>
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-4">
          {artworks.map((art, i) => (
            <button
              key={art.id}
              onClick={() => onOpen(i)}
              data-testid={`${testIdPrefix}-slide-${i}`}
              className="flex-[0_0_82%] sm:flex-[0_0_46%] lg:flex-[0_0_31%] min-w-0 text-left group"
              aria-label={art.title ? `View ${art.title}` : `View artwork ${i + 1}`}
            >
              <div className="border border-white/10 overflow-hidden bg-charcoal">
                <ArtImage
                  art={art}
                  size="md"
                  sizes="(max-width: 640px) 82vw, (max-width: 1024px) 46vw, 31vw"
                  className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.02]"
                  testId={`${testIdPrefix}-image-${i}`}
                />
              </div>
              <div className="flex items-baseline justify-between mt-3">
                <span className="text-xs tracking-[0.2em] text-smoke font-semibold">
                  {art.title || "UNTITLED"}
                </span>
                <span className="font-display text-smoke text-sm">
                  {String(i + 1).padStart(2, "0")} / {String(artworks.length).padStart(2, "0")}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between mt-5">
        <span className="text-[10px] tracking-[0.3em] text-smoke" data-testid={`${testIdPrefix}-position`}>
          {String(selected + 1).padStart(2, "0")} — {String(artworks.length).padStart(2, "0")} · SWIPE
        </span>
        <div className="flex gap-2">
          <button
            onClick={() => emblaApi?.scrollPrev()}
            disabled={!canPrev}
            data-testid={`${testIdPrefix}-prev`}
            aria-label="Previous artwork"
            className="w-11 h-11 border border-white/15 flex items-center justify-center text-bone disabled:opacity-25 hover:border-pink hover:text-pink transition-colors duration-200"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={() => emblaApi?.scrollNext()}
            disabled={!canNext}
            data-testid={`${testIdPrefix}-next`}
            aria-label="Next artwork"
            className="w-11 h-11 border border-white/15 flex items-center justify-center text-bone disabled:opacity-25 hover:border-pink hover:text-pink transition-colors duration-200"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};
